import React, { useEffect } from 'react';

import type { OptionsFromRequest } from '../../../../core/system/rendering/types.js';
import type { StaticPageData } from '../../../types/binding-types.js';

import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import Footer from '../../components/commonComponents/Footer.js';
import { prepareStaticComponent } from '../staticPages/Components.js';
import {
  buildErrorPageView,
  buildStaticPageView,
  defaultByfunnPulseConfig,
} from '../../../tracking/index.js';
import {
  usePulseContext,
  withPulse,
} from '../../../../core/mandatory-integrations/pulse/PulseProvider.js';
import { DataController } from '../../components/commonComponents/DataController.js';

export type StaticPageProps = {
  data: StaticPageData;
  options: OptionsFromRequest;
};

const StaticPage: React.FC<StaticPageProps> = ({ data, options }) => {
  const { siteVersion, currentUrl } = options;
  const { componentName, title, description, pageType } = data;

  const component = prepareStaticComponent(componentName, options);

  const activeSection = currentUrl.split(/[?#]/)[0].substring(1);
  const sectionName = activeSection.split('/').pop();

  const { trackViewListing, trackViewPage, trackViewError } = usePulseContext();

  useEffect(() => {
    if (data.customTrack) {
      return;
    }

    if (componentName === 'error') {
      const { legacy, tpaas } = buildErrorPageView({
        errorCode: 404,
        errorMessage: description ?? title,
        title,
      });

      trackViewError(legacy, tpaas);

      return;
    }

    const result = buildStaticPageView({
      pageType,
      componentName,
      title,
      currentUrl,
      sectionName,
    });

    if (result.pageType === 'Listing') {
      trackViewListing(result.legacy, result.tpaas);
    } else {
      trackViewPage(result.legacy, result.tpaas);
    }
  }, [
    trackViewListing,
    trackViewPage,
    trackViewError,
    data.customTrack,
    componentName,
    description,
    title,
    pageType,
    currentUrl,
    sectionName,
  ]);

  return (
    <div className={`${siteVersion}-front`}>
      <Navigation fullWidth={siteVersion !== 'phone'} />

      {component}

      <Footer isMobile={siteVersion === 'phone'} />
      <DataController />
    </div>
  );
};

const StaticPageWithPulse = withPulse(StaticPage, defaultByfunnPulseConfig);

export { StaticPageWithPulse as StaticPage };
